import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Inbox, Clock, Hourglass, MapPin } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EmptyState, StatCard, DetailDrawer, formatDateTime } from "@/components/admin/ui";
import { IntakeDetailDrawer } from "./IntakeDetailDrawer";
import { STATUS_META, SOURCE_META, channelForContactMethod, contactMethodIcon, serviceAreaLabel } from "./frontDeskMeta";

const FILTER_TABS = [
  { key: "open", label: "Open" },
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "in_review", label: "In Review" },
  { key: "awaiting_customer", label: "Awaiting Customer" },
  { key: "qualified", label: "Qualified" },
  { key: "declined", label: "Declined" },
  { key: "closed", label: "Closed" },
];
const OPEN_STATUSES = new Set(["new", "in_review", "awaiting_customer"]);

export const FrontDeskPanel = () => {
  const { API } = useAuth();
  const [submissions, setSubmissions] = useState(null);
  const [channels, setChannels] = useState({});
  const [salesEnabled, setSalesEnabled] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("open");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const intakeId = searchParams.get("intake");
    if (intakeId) { setSelectedId(intakeId); setFilter("all"); }
  }, [searchParams]);

  const load = () => {
    axios.get(`${API}/org/front-desk/submissions`, { withCredentials: true })
      .then(({ data }) => { setSubmissions(data.submissions); setChannels(data.channels || {}); })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
    axios.get(`${API}/org/front-desk/summary`, { withCredentials: true })
      .then(({ data }) => { setSummary(data.counts); setSalesEnabled(!!data.sales_enabled); })
      .catch(() => {});
  };

  useEffect(() => { load(); }, [API]);

  const onStatusChange = (submissionId, status) => {
    setSubmissions((prev) => prev.map((s) => (s.submission_id === submissionId ? { ...s, status } : s)));
    load();
  };

  const onQualified = (submissionId, data) => {
    setSubmissions((prev) => prev.map((s) => (s.submission_id === submissionId
      ? { ...s, status: "qualified", qualified_lead_id: data.lead_id, qualified_lead_display_id: data.lead_display_id }
      : s)));
    load();
  };

  const selected = submissions?.find((s) => s.submission_id === selectedId);
  const filtered = submissions?.filter((s) => (filter === "all" ? true : filter === "open" ? OPEN_STATUSES.has(s.status) : s.status === filter)) || [];

  return (
    <div className="space-y-6" data-testid="front-desk-workspace">
      {error && <p className="text-sm font-semibold text-red-600 dark:text-red-400" data-testid="front-desk-error">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard icon={Inbox} label="New Intakes" value={summary?.new ?? "—"} tone="blue" testId="front-desk-stat-new" />
        <StatCard icon={Clock} label="In Review" value={summary?.in_review ?? "—"} tone="orange" testId="front-desk-stat-in-review" />
        <StatCard icon={Hourglass} label="Awaiting Customer" value={summary?.awaiting_customer ?? "—"} tone="purple" testId="front-desk-stat-awaiting" />
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-[#111C33]" data-testid="intake-queue-panel">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-bold tracking-tight text-slate-950 dark:text-white">Intake Queue</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Customer requests from every connected intake channel.</p>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap gap-1.5" data-testid="intake-queue-filters">
          {FILTER_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setFilter(t.key)}
              data-testid={`intake-filter-${t.key}`}
              className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors duration-150 ${filter === t.key ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {!submissions ? (
          <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="front-desk-loading">Loading…</p>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Inbox} title="No intakes here yet." message="Once a customer submits your Nova Form or a connected channel comes online, requests will show up here." testId="front-desk-empty" />
        ) : (
          <div className="overflow-x-auto" data-testid="front-desk-submissions-list">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wide text-slate-400 dark:border-white/10">
                  <th className="py-2 pr-3">Customer</th>
                  <th className="py-2 pr-3">Contact</th>
                  <th className="py-2 pr-3">Requested Service</th>
                  <th className="py-2 pr-3">Request</th>
                  <th className="py-2 pr-3">Source</th>
                  <th className="py-2 pr-3">Preferred</th>
                  <th className="py-2 pr-3">Service Area</th>
                  <th className="py-2 pr-3">Received</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => {
                  const sourceMeta = SOURCE_META[s.source] || { label: s.source, icon: MapPin };
                  const SourceIcon = sourceMeta.icon;
                  const channelKey = channelForContactMethod(s.preferred_contact_method);
                  const channelConnected = channelKey ? channels?.[channelKey] : null;
                  const ContactIcon = contactMethodIcon(s.preferred_contact_method);
                  const areaInfo = serviceAreaLabel(s.service_area_result);
                  const statusMeta = STATUS_META[s.status] || { label: s.status, cls: "" };
                  return (
                    <tr
                      key={s.submission_id}
                      onClick={() => setSelectedId(s.submission_id)}
                      data-testid={`front-desk-row-${s.submission_id}`}
                      className="cursor-pointer border-b border-slate-100 hover:bg-slate-50 dark:border-white/5 dark:hover:bg-white/5"
                    >
                      <td className="py-2.5 pr-3">
                        <p className="font-semibold text-slate-900 dark:text-white">{s.first_name} {s.last_name}</p>
                        <p className="font-mono text-[11px] text-slate-400">{s.display_id}</p>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-600 dark:text-slate-300">{s.phone || s.email || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600 dark:text-slate-300">{s.selected_service_name || "—"}</td>
                      <td className="max-w-[160px] truncate py-2.5 pr-3 text-slate-500 dark:text-slate-400" title={s.request_description || ""}>{s.request_description || "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-600 dark:text-slate-300">
                        <span className="inline-flex items-center gap-1"><SourceIcon size={13} className="text-slate-400" />{sourceMeta.label}</span>
                      </td>
                      <td className="py-2.5 pr-3">
                        {s.preferred_contact_method ? (
                          <span className="inline-flex items-center gap-1 text-slate-600 dark:text-slate-300">
                            {ContactIcon && <ContactIcon size={13} className="text-slate-400" />}
                            {s.preferred_contact_method}
                            {channelKey && !channelConnected && <span className="text-amber-500" title={`${channelKey === "sms" ? "SMS" : "Email"} not connected`}>⚠</span>}
                          </span>
                        ) : "—"}
                      </td>
                      <td className={`py-2.5 pr-3 ${areaInfo.cls}`}>{s.service_address ? areaInfo.label : "—"}</td>
                      <td className="py-2.5 pr-3 text-slate-500 dark:text-slate-400">{formatDateTime(s.created_at)}</td>
                      <td className="py-2.5 pr-3">
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${statusMeta.cls}`} data-testid={`front-desk-status-${s.submission_id}`}>{statusMeta.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelectedId(null)}
        title={selected ? `${selected.first_name} ${selected.last_name}` : ""}
        subtitle={selected?.display_id}
        testId="intake-detail"
        wide
      >
        {selected && (
          <IntakeDetailDrawer
            submission={selected}
            channels={channels}
            salesEnabled={salesEnabled}
            onStatusChange={onStatusChange}
            onQualified={onQualified}
            onError={setError}
            API={API}
          />
        )}
      </DetailDrawer>
    </div>
  );
};
